<template>
  <div class="numbered-steps-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row class="numbered-steps-dialog-rows">
        <v-col cols="12">
          <v-text-field
            type="number"
            min="14"
            v-model.number="formData.textSize"
            label="Text size (px)"
            hint="Font size of the step labels (min 14)"
            hide-details="auto"
            :rules="[v => (!!v && v >= 14) || 'Text size must be at least 14']"
            required></v-text-field>
        </v-col>
        <v-col cols="12" class="pt-0">
          <v-checkbox
            v-model="formData.lineEnabled"
            label="Show connecting line"
            hide-details
            class="mt-0 pt-0"></v-checkbox>
        </v-col>
        <v-col v-if="formData.lineEnabled" cols="12">
          <v-text-field
            type="number"
            min="1"
            v-model.number="formData.lineWeight"
            label="Line weight (px)"
            hint="Thickness of the connecting line (min 1)"
            :rules="[v => (!!v && v >= 1) || 'Line weight must be at least 1']"></v-text-field>
        </v-col>
      </v-row>

      <div class="colors-section">
        <VaColorPicker
          :position="'bottom'"
          :title="'Circle fill color'"
          v-model="formData.circleFill"
          class="mb-2"></VaColorPicker>
        <VaColorPicker
          :position="'bottom'"
          :title="'Circle outline color'"
          v-model="formData.circleOutline"
          class="mb-2"></VaColorPicker>
        <VaColorPicker
          :position="'bottom'"
          :title="'Number color'"
          v-model="formData.numberColor"
          class="mb-2"></VaColorPicker>
        <VaColorPicker
          :position="'bottom'"
          :title="'Text color'"
          v-model="formData.textColor"
          class="mb-2"></VaColorPicker>
        <VaColorPicker
          v-if="formData.lineEnabled"
          :position="'bottom'"
          :title="'Line color'"
          v-model="formData.lineColor"
          class="mb-2"></VaColorPicker>
      </div>

      <div v-for="(step, index) in formData.steps" :key="index" class="step-container">
        <div class="d-flex align-center">
          <v-text-field
            type="text"
            v-model="step.text"
            :label="`Step ${index + 1}`"
            class="flex-grow-1 mr-2"
            :rules="[v => !!v || 'Step text is required']"
            required></v-text-field>
          <v-btn icon color="error" @click="removeStep(index)" class="mt-1">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-btn color="primary" class="mt-4" block @click="addStep">
        <v-icon left>mdi-plus</v-icon>
        Add Step
      </v-btn>

      <v-alert v-if="errorMessage" type="error" class="mt-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import { NUMBERED_STEPS_EVENT_TYPES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";

  const NUMBERED_STEPS_DEFAULTS = {
    circleFill: "#2454C7",
    circleOutline: "#2454C7",
    numberColor: "#FFFFFF",
    textColor: "#1A1A1A",
    textSize: 18,
    lineEnabled: true,
    lineColor: "#2454C7",
    lineWeight: 1,
  };

  const getDefaultSteps = () => [
    { text: "Select your age below" },
    { text: "Answer a few questions" },
    { text: "See if you qualify" },
  ];

  export default {
    components: {
      VaColorPicker,
    },
    data() {
      return {
        isFormValid: false,
        formData: {
          ...NUMBERED_STEPS_DEFAULTS,
          steps: getDefaultSteps(),
        },
        errorMessage: "",
      };
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      addStep() {
        this.formData.steps.push({ text: "" });
      },
      removeStep(index) {
        if (this.formData.steps.length > 1) {
          this.formData.steps.splice(index, 1);
        } else {
          this.errorMessage = "At least one step is required";
        }
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: NUMBERED_STEPS_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isFormValid,
            },
          },
          origin,
        );
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: NUMBERED_STEPS_EVENT_TYPES.UPDATE_NUMBERED_STEPS_DATA,
            data: this.formData,
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === NUMBERED_STEPS_EVENT_TYPES.NEED_VALIDATION) {
            await this.$refs.form.validate();
          }
          if (message.data.type === NUMBERED_STEPS_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data === "undefined") {
              this.updateValue();
            } else {
              this.formData = {
                circleFill: data.circleFill || NUMBERED_STEPS_DEFAULTS.circleFill,
                circleOutline: data.circleOutline || NUMBERED_STEPS_DEFAULTS.circleOutline,
                numberColor: data.numberColor || NUMBERED_STEPS_DEFAULTS.numberColor,
                textColor: data.textColor || NUMBERED_STEPS_DEFAULTS.textColor,
                textSize: Math.max(14, data.textSize || NUMBERED_STEPS_DEFAULTS.textSize),
                lineEnabled:
                  typeof data.lineEnabled === "boolean"
                    ? data.lineEnabled
                    : NUMBERED_STEPS_DEFAULTS.lineEnabled,
                lineColor: data.lineColor || NUMBERED_STEPS_DEFAULTS.lineColor,
                lineWeight:
                  typeof data.lineWeight === "number"
                    ? data.lineWeight
                    : NUMBERED_STEPS_DEFAULTS.lineWeight,
                steps: data.steps && data.steps.length ? data.steps : getDefaultSteps(),
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: NUMBERED_STEPS_EVENT_TYPES.REQUEST_DATA,
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";

  .numbered-steps-dialog {
    margin-bottom: 30px;
    padding: 30px 12px 0px;
  }

  .numbered-steps-dialog-rows {
    margin-bottom: 15px;
  }

  .colors-section {
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid get-color("border");
    border-radius: 4px;
  }

  .step-container {
    margin-bottom: 8px;
  }
</style>
